import React, { JSX, useContext } from "react";

import { ActionIcon, Badge, HoverCard, Table, Text, Tooltip } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconRefresh } from "@tabler/icons-react";
import dayjs from "dayjs";

import { TimezoneContext } from "../../App.tsx";
import { MailStatusProperties } from "../../enums/MailStatus.ts";
import { useRetryMail } from "../../hooks/useRetryMail.ts";
import { canRetry, Mail } from "../../models/Mail.ts";
import { Pageable } from "../../models/Pageable.ts";
import { MailDetails } from "../modals/MailDetails.tsx";
import { TableSkeleton } from "../skeletons/TableSkeleton.tsx";

export function MailsTable({
  mails,
  isLoading,
  maxElementsPerPage
}: Readonly<{
  mails: Pageable<Mail> | undefined;
  isLoading: boolean;
  maxElementsPerPage: number;
}>) {
  const browserTimezone = useContext(TimezoneContext);

  const retryMailMutation = useRetryMail();

  const handleRetry = (mailId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    retryMailMutation.mutate(mailId);
  };

  const openMailDetailsModal = (mail: Mail) => {
    modals.open({
      title: <h3 style={{ fontWeight: "bold" }}>{mail.subject ? mail.subject : "[No subject]"}</h3>,
      size: "xl",
      children: (
        <MailDetails
          body={mail.body}
          id={mail.id}
          receiverEmail={mail.receiverEmail}
          senderEmail={mail.senderEmail}
          subject={mail.subject}
          status={mail.status}
          createdAt={mail.createdAt}
          templateCode={mail.templateCode}
          ccs={mail.ccs}
          bccs={mail.bccs}
          attachmentUrls={mail.attachmentUrls}
          lastRetry={mail.lastRetry}
        />
      )
    });
  };
  let contentRows: JSX.Element[] = [];
  if (isLoading) {
    contentRows = [
      <TableSkeleton columnNumber={3} rowNumber={maxElementsPerPage} key="skeleton" />
    ];
  } else if (mails?.content) {
    contentRows = mails.content.map((mail) => (
      <Table.Tr key={mail.id} onClick={() => openMailDetailsModal(mail)}>
        <Table.Td style={{ textAlign: "center", verticalAlign: "middle" }}>
          {canRetry(mail) && (
            <Tooltip label="Retry sending" withArrow>
              <ActionIcon
                variant="light"
                color="blue"
                loading={retryMailMutation.isPending}
                onClick={(event) => handleRetry(mail.id, event)}
              >
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Table.Td>
        <Table.Td>
          <Text size={"lg"}>{mail.subject ? mail.subject : "[No subject]"}</Text>
          <Text size={"sm"} style={{ color: "darkgray" }}>
            Sent to <span style={{ color: "gray" }}>{mail.receiverEmail}</span>{" "}
            {mail.createdAt
              ? dayjs(
                  dayjs(mail.createdAt).tz(browserTimezone).format("YYYY-MM-DD HH:mm:ss")
                ).fromNow()
              : "Unknown"}
            {mail.lastRetry && (
              <>
                {" — Last retry "}
                {dayjs(
                  dayjs(mail.lastRetry).tz(browserTimezone).format("YYYY-MM-DD HH:mm:ss")
                ).fromNow()}
              </>
            )}
          </Text>
        </Table.Td>
        <Table.Td style={{ textAlign: "center", verticalAlign: "middle" }}>
          <HoverCard width={280} shadow="md" openDelay={500}>
            <HoverCard.Target>
              <Badge color={MailStatusProperties[mail.status].color} variant="light">
                {MailStatusProperties[mail.status].value}
              </Badge>
            </HoverCard.Target>
            <HoverCard.Dropdown>
              <Text size="sm">{MailStatusProperties[mail.status].description}</Text>
            </HoverCard.Dropdown>
          </HoverCard>
        </Table.Td>
      </Table.Tr>
    ));
  }

  return (
    <Table highlightOnHover>
      <Table.Tbody>{contentRows}</Table.Tbody>
    </Table>
  );
}
